import { getAuth } from "firebase/auth";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import * as APITypes from "~/API";
import {
  apiClient,
  type LeaseListLoaderResult,
} from "~/services/apiClient";
import { buildHomePendingLeases, type HomePendingLease } from "../utils/buildHomePendingLeases";
import { getShadowInviteResumeUrl } from "~/utils/shadowInviteStorage";
import { ROUTES } from "~/utils";

export type HomeInProgressMaintenance = {
  id: string;
  title: string;
};

export type HomePageLoaderData = {
  inProgressMaintenance: HomeInProgressMaintenance[];
  leaseEndDate: string | null;
  pendingLeases: HomePendingLease[];
};

export type { HomePendingLease };

function isInProgressServiceRequest(
  status: APITypes.ServiceRequestStatusType | null | undefined
): boolean {
  return status === APITypes.ServiceRequestStatusType.Running;
}

function pickActiveLeaseEndDate(
  leaseListApproved: LeaseListLoaderResult["leaseListApproved"]
): string | null {
  const active = leaseListApproved.find(
    (item) => item.leaseInfo?.status === APITypes.LeaseStatusType.Active
  );
  const endDate = active?.leaseInfo?.endDate?.trim();
  return endDate || null;
}

export const HomePageLoader = async ({
  request: _request,
}: LoaderFunctionArgs): Promise<HomePageLoaderData | ReturnType<typeof redirect>> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return redirect(ROUTES.signup);

  const resumeInvite = getShadowInviteResumeUrl();
  if (resumeInvite) {
    return redirect(resumeInvite);
  }

  const [serviceRequestResponse, leaseResponse] = await Promise.all([
    apiClient.loader<{ serviceRequests: APITypes.ServiceRequest[] }>(
      "service-request-table"
    ),
    apiClient.loader<LeaseListLoaderResult>("lease-table"),
  ]);

  const inProgressMaintenance = (
    serviceRequestResponse.data?.serviceRequests ?? []
  )
    .filter((sr) => sr && isInProgressServiceRequest(sr.status))
    .map((sr) => ({
      id: sr.id,
      title: sr.title?.trim() || sr.id,
    }));

  const leaseEndDate = leaseResponse.data
    ? pickActiveLeaseEndDate(leaseResponse.data.leaseListApproved)
    : null;

  const pendingLeases = buildHomePendingLeases(leaseResponse.data ?? undefined);

  return {
    inProgressMaintenance,
    leaseEndDate,
    pendingLeases,
  };
};
